import {useEffect, useRef, useState} from "react";
import {useParams, Link} from "react-router-dom";
import {useSelector} from "react-redux";
import HeaderLayout from "@/components/common/CustomLayout/HeaderLayout";
import './index.css'
import {
  ReactComponent as RiPrinterLine
} from "@/assets/icons/ri-printer-line.svg";
import {
  ReactComponent as RiDownloadLine
} from "@/assets/icons/ri-download-line.svg";
import {
  ReactComponent as JayapranaLogo
} from '@/assets/icons/jayaprana-logo.svg'
import {RootState, useAppDispatch} from "@/store";
import {
  getInvoiceDetail,
  getRefundByInvoiceId,
} from "@/store/invoice/action";
import {getUserVenues} from "@/store/user-info/action";
import {PAYMENT_METHODS, colors} from "@/pages/Invoice/constant";
import {currencyFormat, getLongDateFormat, groupBookingHours, hourFormat} from "@/utils";
import {invoiceActions} from "@/store/invoice/slice";

const html2pdf = require('html2pdf.js')


const renderSchedules = (bookingDetails: InvoiceBookingDetail[], venue: Venue) => {
  const mappedFieldId: InvoiceDetailScheduleRowVO[] = bookingDetails
    .reduce((acc, curr): InvoiceDetailScheduleRowVO[] => {
      const {venue_court_id, id} = curr;
      const existingDateIndex = acc
        .findIndex((item) => item.venue_court_id === venue_court_id);
      const hour = {
        id,
        start_time: curr.start_time,
        end_time: curr.end_time
      } as InvoiceDetailScheduleHourRowVO
      if (existingDateIndex === -1) {
        acc.push({
          venue_court_id,
          courtName: venue.courts.find(c => c.id === venue_court_id)?.code,
          hours: [hour]
        });
      } else {
        acc[existingDateIndex].hours.push(hour)
      }
      return acc;
    }, []);
  
  return <td className='w-full text-sm py-6 flex flex-col gap-4'>
    {
      mappedFieldId.map(f => (
        <div key={f.venue_court_id} className="text-jsm font-medium">
          {f.courtName}
          <ul>
            {
              Object.values(groupBookingHours(
                f.hours.map((hour) => hour.start_time)
              )).map(h =>
                <li key={`${f.venue_court_id}_${h.start}`}
                    className="ml-5 list-disc text-jsm text-grey-dark font-normal">
                  {hourFormat(h.start)} - {hourFormat(h.end)}
                </li>
              )
            }
          </ul>
        </div>
      ))
    }
  </td>
}

const renderInvoiceDetail =
  (invoiceDetailRows: InvoiceDetailRowVO[], venue: Venue) => (
    invoiceDetailRows.map(idr => (
      <tr key={idr.date} className="border-b border-stroke-subtle">
        <td
          className="text-jsm font-bold py-6">
          {getLongDateFormat(idr.date)}
        </td>
        {renderSchedules(idr.schedules, venue)}
        <td
          className='text-sm py-6 font-medium'>{idr.quantity} Hours
        </td>
        <td
          className="text-right pr-6 text-sm font-bold py-6">
          {currencyFormat(idr.price)}
        </td>
      </tr>
    ))
  )

const InvoiceDetail = () => {
  const {invoiceId} = useParams()
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch()
  // const navigate = useNavigate()
  const invoiceData = useSelector((state: RootState) =>
    state.invoice.state.detail)
  const refundsHistory = useSelector((state: RootState) =>
    state.invoice.state.refundsHistory)
  const [status, setStatus] = useState(invoiceData?.status ?? 'UNPAID')
  const venues = useSelector((state: RootState) =>
    state.userInfo.state.venues)
  
  const getVenue = () => venues.find(v => v.id === invoiceData.venue_id)
  
  const handleClickPrint = () => {
    window.print()
  }

  const handleClickDownload = () => {
    const element = document.getElementById('invoice-detail-download');
    html2pdf()
      .set({ 
        filename: `${invoiceData.custom_id}.pdf`,
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        image: { type: 'jpeg', quality: 1 },
        html2canvas: {
          dpi: 192,
          scale:4,
          letterRendering: true,
          useCORS: true
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
       })
      .from(element, 'element')
      .toPdf()
      .save()
  }
  
  const refundedInvoiceDetails = invoiceData?.invoice_detail
    .filter(id => id.refund_detail)
  
  const mappedInvoiceDetailRow = invoiceData?.invoice_detail
    ?.reduce((acc, curr): InvoiceDetailRowVO[] => {
      const {date} = curr;
      const existingDateIndex = acc.findIndex((item) => item.date === date);
      if (existingDateIndex === -1) {
        acc.push({
          date,
          price: curr.price,
          quantity: curr.booking_detail.end_time - curr.booking_detail.start_time,
          schedules: [curr.booking_detail]
        });
      } else {
        acc[existingDateIndex].schedules.push(curr.booking_detail);
        acc[existingDateIndex].quantity += curr.booking_detail.end_time - curr.booking_detail.start_time
        acc[existingDateIndex].price += curr.price
      }
      return acc;
    }, []);
  
  const checkForRefunds = () => {
    if (!refundedInvoiceDetails?.length) {
      return
    }
    dispatch(getRefundByInvoiceId({invoiceId}))
  }
  
  useEffect(() => {
    setStatus(invoiceData?.status ?? 'UNPAID')
    checkForRefunds()
  }, [invoiceData])
  
  useEffect(() => {
    dispatch(invoiceActions.resetInvoice())
    dispatch(getInvoiceDetail(invoiceId))
    dispatch(getUserVenues({}))
  }, [])
  
  return (
    <HeaderLayout title="Invoice Detail">
      {invoiceData && getVenue() && mappedInvoiceDetailRow &&
        <div ref={ref} className="detail-wrapper print-screen" id="invoice-detail">
          <div className="flew w-full flex-col card">
            <div className="flex justify-between w-full py-9 px-7 print-none">
              <div className="text-jlg font-bold">Invoice</div>
              <div className="flex print-hidden">
                <button
                  onClick={handleClickPrint}
                  className="cursor-pointer text-red-primary text-jsm font-medium flex gap-2 justify-center items-center py-2 px-6">
                  <RiPrinterLine/>
                  Print
                </button>
                <button
                  onClick={handleClickDownload}
                  className="cursor-pointer text-black-primary text-jsm font-medium flex gap-2 justify-center items-center py-2 px-6 rounded border border-stroke-subtle">
                  <RiDownloadLine/>
                  Download
                </button>
              </div>
            </div>
            <div className="horizonal-separator"/>
            <div className="static py-12 px-7 flex flex-col w-full gap-6" id="invoice-detail-download">
              <div className="w-full flex justify-between">
                <div className="text-jb-3xl w-1/2">
                  {invoiceData.custom_id}
                  <div className={`mt-4 p-2 rounded w-40 text-center text-jlg font-bold ${colors[status]}`}>
                  {invoiceData.status}
                  </div>
                  <p className="text-jsm text-grey-dark mt-2 underline">via: {PAYMENT_METHODS[invoiceData.payment_method_id]}</p>
                </div>
                <div className="flex flex-col w-full justify-end items-end">
                  <JayapranaLogo/>
                  <>
                    <div className="text-jlg font-bold">
                      {getVenue().name}
                    </div>
                    <div className="text-jsm text-right">{getVenue().address}</div>
                  </>
                  <div className="text-jsm text-grey-dark">
                    {getLongDateFormat(invoiceData.created_at)}
                  </div>
                </div>
              </div>
              <div className="flex flex-col w-full">
                <div className="text-jlg">BILL TO</div>
                <div
                  className="text-jbase font-bold">{invoiceData?.customer?.name}</div>
                <div
                  className="text-jbase text-grey-dark">{invoiceData?.customer?.phone_number}</div>
                <div
                  className="text-jbase text-grey-dark">{invoiceData?.customer?.email}</div>
              </div>
              <table>
                <thead>
                <tr className="bg-grey-subtle text-grey-dark">
                  <th className="pl-6">DATE</th>
                  <th>SCHEDULE</th>
                  <th>QTY</th>
                  <th className="text-right pr-6">SUBTOTAL</th>
                </tr>
                </thead>
                <tbody>
                {renderInvoiceDetail(mappedInvoiceDetailRow, getVenue())}
                </tbody>
              </table>
              <div className="w-full flex flex-col justify-center items-end">
                <div
                  className="flex w-1/2 text-jbase text-grey-dark font-normal justify-between">
                  <div>Sub Total</div>
                  <div>{currencyFormat(mappedInvoiceDetailRow.reduce((acc, curr) =>
                    acc + curr.price, 0))}</div>
                </div>
                { invoiceData?.booking.rescheduled_from &&
                  <div
                    className="flex w-1/2 text-jbase text-grey-dark font-normal justify-between">
                    <div>Paid From <span className="text-red-primary">#{invoiceData?.booking.rescheduled_from.from_custom_id}</span></div>
                    <div>-{currencyFormat(invoiceData?.booking.rescheduled_from.paid_amount)}</div>
                  </div>
                }
                <div
                  className="flex w-1/2 text-jbase text-grey-dark font-normal justify-between">
                  <div>Voucher 
                  </div>
                  <div>-{currencyFormat(invoiceData?.discount_amount ?? 0)}</div>
                </div>
                <div
                  className="flex w-1/2 text-jbase text-grey-dark font-normal justify-between">
                  <div>Local Tax {getVenue().tax_fee}%</div>
                  <div>{currencyFormat(invoiceData?.tax_amount ?? 0)}</div>
                </div>
                { invoiceData?.booking.rescheduled_from &&
                  <div
                    className="flex w-1/2 text-jbase text-grey-dark font-normal justify-between">
                    <div>Reschedule Fee</div>
                    <div>{currencyFormat(invoiceData?.booking.rescheduled_from.fee)}</div>
                  </div>
                }
                <div
                  className="flex w-1/2 pt-2 text-jbase font-bold justify-between">
                  <div>TOTAL</div>
                  <div>{currencyFormat(invoiceData.total_price)}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="print-full flex flex-col w-[400px] gap-6 print-none">
            <div className="card p-6 flex flex-col gap-6">
              <div className="w-full grid grid-cols-2">
                <div className="text-jlg">Status</div>
                <div
                  className={`px-4 py-1 w-full rounded capitalize font-medium text-center
                  ${colors[status]}`}>
                  {status.replace('_',  ' ')}
                </div>
                  {
                /*
                <div>
                 <select
                    multiple={false}
                    onChange={handleStatusChange}
                    className={`p-2 w-full ${colors[status]}`}
                    name="invoice-status"
                    disabled
                    value={status}>
                    {
                      statusOptions.map(i =>
                        <option
                          key={i}
                          value={i}
                          className={colors[i]}
                        >
                          {i.replace('_', ' ')}
                        </option>
                      )
                    }
                  </select> 
                </div>
                */}
              </div>
              {/* <Button type={null} className="w-full" variant="primary"
                      onClick={handleUpdate}>
                Update
              </Button> */}
            </div>
            <div className="card p-7 flex flex-col w-full gap-7">
              <div className="text-jlg font-bold">Reference</div>
              <div className="flex flex-col gap-4 w-full">
                <div className="w-full grid grid-cols-2">
                  <div className="text-jsm">Booking ID</div>
                  <Link
                    to={`/dashboard/booking/${invoiceData?.booking.id}/edit`}
                    className="text-jsm text-red-primary underline text-right cursor-pointer"
                  >
                    {invoiceData?.booking.custom_id}
                  </Link>
                </div>
                {invoiceData?.subscription_id &&
                  <div className="w-full grid grid-cols-2">
                    <div className="text-jsm">Subscription ID</div>
                    <Link
                      to={`/dashboard/subscription/edit/${invoiceData?.subscription.id}`}
                      className="text-jsm text-red-primary underline text-right cursor-pointer">
                      {invoiceData?.subscription.custom_id}
                    </Link>
                  </div>}
              </div>
              {
                refundsHistory.length > 0 &&
                <>
                  <div className="horizontal-separator"/>
                  <div className="text-jlg font-bold">Refund History</div>
                  {
                    refundsHistory.map((r) => (
                      <div key={r.id} className="w-full grid grid-cols-2">
                        <div
                          className="text-jsm font-normal">{getLongDateFormat(r.created_at, 'id-ID')}</div>
                        <Link
                          to={`/dashboard/refund?search=${r.custom_id}`}
                          className="text-jsm font-medium text-red-primary underline text-right">
                          {r.custom_id}
                        </Link>
                      </div>
                    ))
                  }
                </>
              }
            </div>
          </div>
        </div>}
    </HeaderLayout>
  );
};

export default InvoiceDetail;